import { useEffect, useState } from 'react';

import { scadenze as apiScadenze } from '../api/scadenziario.ts';
import type { Scadenza, VoceStorico } from '../api/tipi-dati.ts';
import { BadgeSituazione } from './BadgeSituazione.tsx';
import { Dialogo, RiepilogoScadenza } from './Dialogo.tsx';
import { PannelloAllegati } from './PannelloAllegati.tsx';
import { etichettaAzione } from './attivita.ts';
import { dataBreve, istante } from './formato.ts';

/**
 * La scheda di una scadenza: tutto quello che c'è, **senza poter cambiare
 * niente**.
 *
 * Nasce da un'osservazione di Enrico (7 agosto): per guardare una scadenza
 * bisognava passare da «Modifica», cioè aprire un form. Un form invita a
 * cambiare qualcosa, e chi consultava — che è la maggioranza — si trovava a
 * usare lo strumento sbagliato, con il rischio di lasciarci dentro una
 * modifica involontaria.
 *
 * Qui non c'è nessun campo compilabile. L'unica eccezione sono gli allegati,
 * che si aggiungono e si tolgono: vedi il commento in `PannelloAllegati`.
 */
export function DialogoDettaglio({
  scadenza: dallElenco,
  onChiudi,
  onModifica,
}: {
  scadenza: Scadenza;
  onChiudi: () => void;
  /** Il ponte verso la modifica, per chi si accorge guardando che c'è un errore. */
  onModifica: () => void;
}) {
  const [storico, setStorico] = useState<VoceStorico[] | null>(null);
  /**
   * La scheda intera, riletta dal server.
   *
   * ⚠️ **La riga dell'elenco non basta**, e il motivo è nel contratto delle
   * API: `destinatari` esiste solo sulla singola scadenza, perché nell'elenco
   * sarebbe una query per riga per un dato che la tabella non mostra. Fidarsi
   * dell'oggetto arrivato da lì faceva comparire «Chi viene avvisato» sempre
   * vuoto, anche quando i destinatari c'erano — trovato da Enrico a schermo il
   * 7 agosto, non dai test: nessuno dei due lati mentiva, mancava la lettura.
   *
   * Nell'attesa si mostra la riga dell'elenco: ha già soggetto, tipo, data e
   * situazione, quindi la scheda si apre piena invece di lampeggiare vuota.
   */
  const [completa, setCompleta] = useState<Scadenza | null>(null);
  const scadenza = completa ?? dallElenco;

  useEffect(() => {
    let annullato = false;

    apiScadenze
      .ottieni(dallElenco.id)
      .then((s) => { if (!annullato) setCompleta(s); })
      .catch(() => undefined);

    apiScadenze
      .storico(dallElenco.id)
      // Lo storico è un di più: se non arriva, la scheda resta utile. Si
      // preferisce non mostrarlo piuttosto che coprire i dati con un errore.
      .then((r) => { if (!annullato) setStorico(r.voci); })
      .catch(() => { if (!annullato) setStorico([]); });

    return () => { annullato = true; };
  }, [dallElenco.id]);

  // Il nome arriva già pronto dal backend, «Senza categoria» compreso: qui
  // serve solo sapere se c'è una categoria vera, per cambiare il tono.
  const senzaCategoria = scadenza.tipo.categoria.id === null;

  return (
    <Dialogo titolo="Dettaglio della scadenza" aperto onChiudi={onChiudi}>
      <RiepilogoScadenza
        soggetto={scadenza.soggetto.denominazione}
        soggettoEliminato={scadenza.soggetto.eliminato}
        codice={scadenza.soggetto.codice}
        reparto={scadenza.soggetto.reparto}
        tipo={scadenza.tipo.nome}
        tipoEliminato={scadenza.tipo.eliminato}
        dataPrevista={dataBreve(scadenza.dataScadenza)}
        giorni={scadenza.giorniMancanti}
      />

      <dl className="dettaglio-scheda">
        <dt>Situazione</dt>
        <dd><BadgeSituazione situazione={scadenza.situazione} /></dd>

        <dt>Categoria</dt>
        <dd>
          {senzaCategoria ? (
            /* Non è un vuoto da nascondere: è una cosa da risistemare, e il
               cruscotto la conta apposta sotto «Senza categoria». */
            <span className="tenue">Senza categoria — da risistemare</span>
          ) : (
            scadenza.tipo.categoria.nome
          )}
        </dd>

        <dt>Preavviso</dt>
        <dd>
          {scadenza.preavvisoGiorni === 0
            ? 'Nessun preavviso: si segnala solo quando è scaduta'
            : `${scadenza.preavvisoGiorni} giorni prima della data`}
        </dd>

        {scadenza.periodicitaMesi !== null && (
          <>
            <dt>Si ripete</dt>
            <dd>ogni {scadenza.periodicitaMesi} mesi</dd>
          </>
        )}

        <dt>Descrizione</dt>
        <dd>{scadenza.descrizione ?? <span className="tenue">—</span>}</dd>

        <dt>Note</dt>
        <dd className="dettaglio-note">
          {scadenza.note ?? <span className="tenue">—</span>}
        </dd>

        {scadenza.stato === 'COMPLETATA' && (
          <>
            <dt>Completata</dt>
            <dd>
              {scadenza.dataCompletamento !== null && dataBreve(scadenza.dataCompletamento)}
              {scadenza.completataDa !== null && (
                <span className="tenue"> · da {scadenza.completataDa.nome}</span>
              )}
            </dd>
          </>
        )}

        {scadenza.stato === 'ANNULLATA' && (
          <>
            <dt>Annullata</dt>
            <dd>{scadenza.motivoAnnullamento ?? <span className="tenue">senza motivo indicato</span>}</dd>
          </>
        )}

        {scadenza.scadenzaPrecedenteId !== null && (
          <>
            <dt>Origine</dt>
            <dd className="tenue">
              Creata dal rinnovo automatico di una scadenza completata
            </dd>
          </>
        )}

        <dt>Chi viene avvisato</dt>
        <dd>
          {completa === null ? (
            <span className="tenue">…</span>
          ) : scadenza.muta === true ? (
            /* Il caso che va detto forte: comprende sia «nessun nome» sia
               «nomi che hanno gli avvisi spenti», perché il risultato è lo
               stesso e distinguerli qui rassicurerebbe a torto. */
            <span className="senza-destinatari">
              🔕 Nessuno — questa scadenza non farà squillare niente
            </span>
          ) : (
            (scadenza.destinatari ?? []).map((d, i) => (
              <span key={d.id}>
                {i > 0 && ', '}
                {d.nome}
                {!d.riceveDavvero && <span className="tenue"> (avvisi spenti)</span>}
              </span>
            ))
          )}
        </dd>

        <dt>Registrata</dt>
        <dd className="tenue">
          {istante(scadenza.creataIl)}
          {scadenza.modificataIl !== scadenza.creataIl &&
            ` · ultima modifica ${istante(scadenza.modificataIl)}`}
        </dd>
      </dl>

      <PannelloAllegati scadenzaId={scadenza.id} />

      {storico !== null && storico.length > 0 && (
        <section className="dettaglio-storico">
          <h3 className="sezione-titolo">Cosa è successo</h3>
          <ul className="elenco-storico">
            {storico.map((v, i) => (
              // Il registro non espone un id: la coppia data+indice è stabile
              // quanto basta per una lista che non si riordina.
              <li key={`${v.avvenutoIl}-${i}`}>
                <span className="storico-quando">{istante(v.avvenutoIl)}</span>
                {/* «Carica allegato», non `CARICA_ALLEGATO`: il codice
                    dell'azione è dell'applicazione, non di chi legge. Stessa
                    funzione della pagina Attività, non una seconda copia. */}
                <span className="storico-cosa">{etichettaAzione(v.azione)}</span>
                <span className="tenue">{v.utente ?? 'autore non più registrato'}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="dialogo-azioni">
        <button type="button" className="bottone bottone-principale" onClick={onChiudi}>
          Chiudi
        </button>
        {/*
          Il passaggio alla modifica resta, ma è una scelta esplicita e non più
          l'unica strada per guardare: chi si accorge leggendo che una data è
          sbagliata non deve chiudere e ricominciare dal menù.
        */}
        <button type="button" className="bottone-piatto" onClick={onModifica}>
          Modifica questa scadenza
        </button>
      </div>
    </Dialogo>
  );
}
